import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form15397Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 15397 Instructions | Recipient Deadline Extension"
          description="Get complete Form 15397 instructions to request a 30-day extension for furnishing recipient copies of information returns. File accurately with TaxZerone."
          keywords=""
          canonical="resources/tax-extension/form-15397-instructions/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 15397</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Form 15397 with TaxZerone and get a 30-day extension to send your
          recipient statements.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=15397instructions`}
          >
            File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-15397-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 15397 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}> Form 15397 </span> is
                        filed with the IRS by issuers and transmitters to
                        request a
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          30-day extension
                        </span>{' '}
                        who need additional time to furnish recipient copies of
                        certain information returns.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}> Due Date:</span> Form
                        15397 must be submitted on or before the original
                        recipient statement due date and cannot be filed before
                        January 1 of the filing year.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 15397 allows businesses to request additional time to
                  issue recipient copies of certain information returns. If you
                  need more time to distribute recipient copies of Forms such as
                  W-2, W-2G, 1042-S, 1095, 1097, 1098, 1099, 3921, 3922, and
                  5498, the Form 15397 helps you avoid penalties by requesting
                  an extension.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This resource will help you understand who should file, when
                  to file, and how to complete Form 15397 accurately.
                </Typography>
                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 15397?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who should file Form 15397?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to File Form 15397?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What information do you need to file Form 15397 IRS?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-Step instructions on how to complete Form
                          15397
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Submit Form 15397 by Fax
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What happens after you file IRS 15397 Form?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is Form 15397
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 15397 is used to request a{' '}
                  <span style={{ fontWeight: 600 }}> one-time extension</span>{' '}
                  of up to <span style={{ fontWeight: 600 }}>30 days</span> to
                  furnish recipient statements for certain information returns
                  to recipients for the current tax year. This extension applies
                  only to the recipient copies, not to the IRS filing deadline.
                </Typography>
                <Box
                  component="div"
                  sx={{ mt: 3, mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 0, fontSize: 18 }}
                      >
                        Need more time to furnish recipient copies?
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: 15 }}>
                        File Form 15397 through TaxZerone and get your 30-day
                        extension today.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=15397instructions`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who should file Form 15397?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Issuer/Transmitter should{' '}
                  <Link
                    href="/tax-extension/file-irs-form-15397-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 15397
                  </Link>{' '}
                  if:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Need more time to send{' '}
                    <span style={{ fontWeight: 600 }}> recipient copies</span>{' '}
                    of information returns (like Forms W-2, 1099s, or 5498s).
                  </li>
                  <li>
                    Circumstances such as missing information or unexpected
                    business issues prevent you from furnishing forms on
                    time.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="when"
                >
                  When to File Form 15397?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 15397 should be filed{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    on or before the original due date but not before January 1
                  </span>{' '}
                  to furnish the recipient copies. The forms and their due dates
                  are:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 400,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Recipient’s Due Date*
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1042-S
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1095-B, 1095-C
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1098, 1099, 3921, 3922, W-2G
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1097
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> Q1:</span> May 15
                            <br style={{ marginBottom: 8 }} />{' '}
                            <span style={{ fontWeight: 600 }}>Q2:</span> August
                            15 <br style={{ marginBottom: 8 }} />
                            <span style={{ fontWeight: 600 }}>Q3</span> November
                            15 <br style={{ marginBottom: 8 }} />
                            <span style={{ fontWeight: 600 }}>Q4:</span>{' '}
                            February 15
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-NEC
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-QA
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            W-2
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            For FMV/RMD/ SIMPLE IRA contributions:{' '}
                            <span style={{ fontWeight: 600 }}>January 31</span>{' '}
                            <br style={{ marginBottom: 8 }} />
                            For all other contributions:{' '}
                            <span style={{ fontWeight: 600 }}>May 31</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498-ESA
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            April 30
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498-QA
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            5498-SA
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            May 31
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  *If the deadline falls on a weekend or legal holiday, the next
                  business day will be considered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS grants a{' '}
                  <span style={{ fontWeight: 600 }}> 30-day extension</span> if
                  the form is filed correctly, and the reason for filing an
                  extension is valid.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> This form does not extend the time to file with
                  the IRS. To extend the IRS filing deadline, you must
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>
                  <Link
                    href="/tax-extension/efile-irs-form-8809-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 8809
                  </Link>{' '}
                  separately.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What information do you need to file IRS Form 15397?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To complete Form 15397, the following details are required:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>Issuer’s/ Transmitter’s legal name</li>
                  <li>Taxpayer Identification Number (TIN) </li>
                  <li>Address and contact details </li>
                  <li>
                    Types of forms for which you’re requesting an extension{' '}
                  </li>
                  <li>Reason for requesting an extension </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="step"
                >
                  Step-by-Step instructions on how to complete Form 15397:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The instructions below will guide you on filing Form 15397
                  accurately with the IRS.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f15397-issuer-img.webp" />
                  <source srcSet="/assets/images/f15397-issuer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f15397-issuer-img.png"
                    loading="lazy"
                    alt="Form 15397 – Lines 1 and 2: Issuer’s/Transmitter’s information and TIN"
                    width={753}
                    height={183}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Issuer’s/Transmitter’s name, complete address such
                  as City, State, and ZIP code. Also, enter the Contact name,
                  Telephone number, and Email address of the person the IRS may
                  contact regarding this form
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the issuer’s nine-digit Employer Identification Number
                    (EIN), Qualified Intermediary EIN (QI-EIN), Withholding
                    Foreign Partnership EIN (WP-EIN), or Withholding Foreign
                    Trust EIN (WT-EIN).
                  </li>
                  <li>
                    If you don’t have an EIN or QI-EIN, enter your Social
                    Security Number (SSN).
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f15397-extension-img.webp" />
                  <source srcSet="/assets/images/f15397-extension-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f15397-extension-img.png"
                    loading="lazy"
                    alt="Form 15397 – Line 3: If requesting an extension for more than one issuer/transmitter, enter the count."
                    width={755}
                    height={71}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    If you are using a single Form 15397 to request an extension
                    for many issuers or transmitters, include a typed list of
                    each name and TIN along with the total number of issuers or
                    transmitters.
                  </li>
                  <li>
                    This field should be left empty if you are filing a
                    different form 15397 for each issuer or transmitter.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The list must be in a 12-point font and clearly
                  readable.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f15397-check-only-img.webp" />
                  <source srcSet="/assets/images/f15397-check-only-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f15397-check-only-img.png"
                    loading="lazy"
                    alt="Form 15397 – Line 4: Check the box that applies. "
                    width={760}
                    height={175}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the boxes of the information return types for which you
                  need more time to furnish{' '}
                  <span style={{ fontWeight: 600 }}> recipient copies,</span>{' '}
                  such as:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <Link
                      href="/resources/information-returns/form-1042-s-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      1042-S{' '}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-1095-b-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      1095-B
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/information-returns/form-1095-c-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      1095-C
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-1097-btc-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      1097
                    </Link>
                    ,{' '}
                    <Link
                      href="/information-returns/efile-irs-form-1098/"
                      sx={{ color: '#0202ff' }}
                    >
                      1098
                    </Link>
                    ,{' '}
                    <Link href="/form-1099-series/" sx={{ color: '#0202ff' }}>
                      1099
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/information-returns/form-3921-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      3921
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/information-returns/form-3922-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      3922
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/information-returns/form-w-2g-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      W-2G
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-1099-nec-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      1099-NEC
                    </Link>{' '}
                  </li>
                  <li>1099-QA </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-w-2-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      W-2
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-5498-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      5498
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-5498-esa-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      5498-ESA
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-5498-qa-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      5498-QA
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/information-returns/form-5498-sa-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      5498-SA
                    </Link>{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f15397-furnish-recipient-img.webp" />
                  <source srcSet="/assets/images/f15397-furnish-recipient-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f15397-furnish-recipient-img.png"
                    loading="lazy"
                    alt="Form 15397 – Line 5: Describe the need for an extension to furnish recipient copies of information returns."
                    width={760}
                    height={314}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked any boxes on Line 4, explain why you need more
                  time to send the recipient copies of the information returns.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f15397-under-penalty-img.webp" />
                  <source srcSet="/assets/images/f15397-under-penalty-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f15397-under-penalty-img.png"
                    loading="lazy"
                    alt="Form 15397 –Enter your signature, title, and date"
                    width={760}
                    height={90}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  To confirm that the given details are true, enter the
                  signature and title of the issuer along with the date.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to Submit Form 15397 by Fax?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you prefer to submit your Form 15397 by fax,
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Send your completed and signed form to the IRS using the
                    official fax numbers below:
                  </li>
                  <li>
                    <Box component="div">
                      <span style={{ fontWeight: 600 }}> Fax to:</span>
                      <br /> Internal Revenue Service Technical Services
                      Operation <br />
                      Attn: Extension of Time Coordinator 877-477-0572
                      <br />
                      (International: 304-579-4105)
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="happen"
                >
                  What happens after you file IRS 15397 Form?
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    If Form 15397 is approved by the IRS, you receive a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      30-day extension
                    </span>{' '}
                    to furnish recipient copies.{' '}
                  </li>
                  <li>
                    If the IRS denies your request, you must furnish the copies
                    by the original due date to avoid penalties.
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  File Form 15397 with TaxZerone
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> IRS Error Checks:</span>{' '}
                    TaxZerone checks your form 15397 for mistakes that could
                    cause your extensions to be denied.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Email Delivery:</span>{' '}
                    Send copies to the recipient by email, so you don&apos;t
                    have to mail them manually.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      ZeroneVault Access:
                    </span>{' '}
                    Share your filed forms securely anytime, anywhere through
                    our encrypted portal.{' '}
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Affordable & Transparent:
                    </span>{' '}
                    Get competitive prices with no extra fees.{' '}
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f15397.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions
                        </Link>{' '}
                        for form 15397.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to extend your recipient deadline?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File Form 15397 with TaxZerone in minutes and stay in compliance
              with the IRS regulations.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=15397instructions`}
              >
                File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form15397Instructions;
